// src/services/fusionAuthService.ts
//
// Thin client for the narrow /api/fusion-auth router. Only the two endpoints
// the backend still exposes are wrapped here: a slim public read and a
// self-only profile update. See documentation/planning/fusion-router-security-audit.md
// for the audit that removed the previous (insecure) admin-style endpoints.

import { getBackendUrl } from './configService';

const getBaseUrl = (): string => {
  const url = getBackendUrl();
  if (!url) {
    throw new Error('❌ FATAL: BACKEND_URL not configured in runtime config');
  }
  return url;
};

const getAuthHeaders = (): HeadersInit => {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('access_token');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

// Slim user shape returned by the backend (matches the server-side `slim()` helper).
export interface FusionAuthUser {
  id: string;
  email: string;
  username?: string;
  firstName: string;
  lastName: string;
  mobilePhone?: string;
}

export interface UserUpdateRequest {
  id: string;
  firstName: string;
  lastName: string;
  mobilePhone?: string;
}

export const getUserById = async (userId: string): Promise<FusionAuthUser> => {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/api/fusion-auth/users/${userId}`, {
    headers: getAuthHeaders(),
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch user: ${response.statusText}`);
  }
  const data = await response.json();
  return data.user;
};

export const updateUser = async (userData: UserUpdateRequest): Promise<FusionAuthUser> => {
  const baseUrl = getBaseUrl();
  const body = {
    user: {
      firstName: userData.firstName,
      lastName: userData.lastName,
      mobilePhone: userData.mobilePhone ?? '',
    },
  };
  const response = await fetch(`${baseUrl}/api/fusion-auth/users/${userData.id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'We could not save your changes. Please try again.');
  }
  const data = await response.json();
  return data.user;
};
